import {
  getGridStringOperators,
  getGridNumericOperators,
  getGridDateOperators,
  getGridSingleSelectOperators,
} from '@mui/x-data-grid-pro';
import type { GridFilterOperator } from '@mui/x-data-grid-pro';
import { OperatorOption } from '../constants';
import { FILTER_INPUT_TYPE, InputTypeByField } from './constants';

// Helper to detect if a field is an EBV-related field (needs numeric treatment)
function isEbvRelatedField(field: string): boolean {
  // Match ebv{n}, ebv{n}Acc, ebv{n}Percentile patterns
  return /^ebv\d+(Acc|Percentile)?$/i.test(field);
}

// Get input type for a field, with dynamic handling for EBV fields
function getInputTypeForField(field: string): string | undefined {
  // EBV-related fields should be treated as numeric
  if (isEbvRelatedField(field)) {
    return FILTER_INPUT_TYPE.NUMBER;
  }
  return InputTypeByField[field];
}

// overwrites MUI filter operators with i6 operators
export function getGridOperatorsByField(field: string) {
  const inputType = getInputTypeForField(field);

  let operators: GridFilterOperator[];
  switch (inputType) {
    case FILTER_INPUT_TYPE.DATE:
      operators = getGridDateOperators();
      break;
    // add non standard options to dropdown type
    case FILTER_INPUT_TYPE.DROPDOWN:
      {
        const base = getGridSingleSelectOperators();
        const equalsOp = base.find((op) => op.value === 'is');
        const notEqualsOp: GridFilterOperator = {
          label: 'is not',
          value: 'notEquals',
          InputComponent: equalsOp?.InputComponent,
          InputComponentProps: equalsOp?.InputComponentProps,
          getApplyFilterFn: (item) => {
            if (item.value == null || item.value === '') return null;
            return (params) => params.value !== item.value;
          },
        };
        const isNotEmptyOp: GridFilterOperator = {
          label: 'is not empty',
          value: 'isNotEmpty',
          getApplyFilterFn: () => {
            return (params) => params.value != null && params.value !== '';
          },
        };
        operators = [equalsOp, notEqualsOp, isNotEmptyOp].filter(
          Boolean,
        ) as GridFilterOperator[];
      }
      break;
    case FILTER_INPUT_TYPE.NUMBER:
      operators = getGridNumericOperators();
      break;
    default:
      const base = getGridStringOperators();
      const isNotEmptyIndex = base.findIndex((op) => op.value === 'isNotEmpty');
      const notEqualsOp: GridFilterOperator = {
        label: 'not Equals',
        value: 'notEquals',
        InputComponent: base[0]!.InputComponent,
        InputComponentProps: base[0]!.InputComponentProps,
        getApplyFilterFn: (item) => {
          if (item.value == null || item.value === '') return null;
          return (params) => params.value !== item.value;
        },
      };
      base.splice(isNotEmptyIndex, 0, notEqualsOp);
      operators = base;
      break;
  }

  const operatorMap = {
    '=': {
      label: OperatorOption.equals.label,
      value: OperatorOption.equals.value,
    },
    '!=': {
      label: OperatorOption.notEquals.label,
      value: OperatorOption.notEquals.value,
    },
    '>': {
      label: OperatorOption.greaterThan.label,
      value: OperatorOption.greaterThan.value,
    },
    '>=': {
      label: OperatorOption.greaterThanOrEqual.label,
      value: OperatorOption.greaterThanOrEqual.value,
    },
    '<': {
      label: OperatorOption.lessThan.label,
      value: OperatorOption.lessThan.value,
    },
    '<=': {
      label: OperatorOption.lessThanOrEqual.label,
      value: OperatorOption.lessThanOrEqual.value,
    },
    is: {
      label: 'Is',
      value: OperatorOption.equals.value,
    },
    equals: OperatorOption.equals,
    not: {
      label: 'Not',
      value: OperatorOption.notEquals.value,
    },
    isNot: {
      label: 'Not',
      value: OperatorOption.notEquals.value,
    },
    notEquals: OperatorOption.notEquals,
    after: {
      label: 'After',
      value: OperatorOption.greaterThan.value,
    },
    before: {
      label: 'Before',
      value: OperatorOption.lessThan.value,
    },
    contains: OperatorOption.contains,
    startsWith: OperatorOption.startsWith,
    endsWith: OperatorOption.endsWith,
    isNotEmpty: OperatorOption.isNotEmpty,
  };

  const remappedOperators = operators.map((op) => {
    const operator = operatorMap[op.value as keyof typeof operatorMap];
    return { ...op, ...operator } as GridFilterOperator;
  });

  const validOperators = getOperatorsByField(field).map((op) => op.value);
  return remappedOperators.filter((op) => validOperators.includes(op.value));
}

export function getOperatorsByField(field: string) {
  switch (getInputTypeForField(field)) {
    case FILTER_INPUT_TYPE.DROPDOWN:
      return [
        OperatorOption.equals,
        OperatorOption.notEquals,
        OperatorOption.isNotEmpty,
      ];

    case FILTER_INPUT_TYPE.DATE:
    case FILTER_INPUT_TYPE.NUMBER:
      return [
        OperatorOption.equals,
        OperatorOption.notEquals,
        OperatorOption.greaterThanOrEqual,
        OperatorOption.greaterThan,
        OperatorOption.lessThanOrEqual,
        OperatorOption.lessThan,
        OperatorOption.between,
        OperatorOption.isNotEmpty,
      ];

    default:
      return [
        OperatorOption.contains,
        OperatorOption.equals,
        OperatorOption.startsWith,
        OperatorOption.endsWith,
        OperatorOption.notEquals,
        OperatorOption.isNotEmpty,
      ];
  }
}
